import { Controller } from '@hotwired/stimulus'

const NOTE_COLOR = '#111827'
const NOTE_RADIUS = 6
const HIT_RADIUS = 10

// Renders the comparison Chart.js chart from a JSON <script> tag and lets
// the summary table's checkboxes toggle dataset visibility. Also overlays
// admin-authored report notes as dots along the baseline (see
// admin/report_notes) so metric movement can be correlated with changes.
export default class extends Controller {
  static targets = ['canvas', 'config', 'masterToggle', 'rowToggle', 'notes']

  connect() {
    this.notesVisible = true

    const config = JSON.parse(this.configTarget.textContent)
    this.bucketDates = config.bucketDates || []
    this.period = config.period
    delete config.bucketDates
    delete config.period

    this.applyCallbacks(config)
    this.updateMarkers()

    config.plugins = [ { id: 'reportNotes', afterDraw: (chart) => this.drawNoteMarkers(chart) } ]
    this.chart = new window.Chart(this.canvasTarget, config)
    this.setupNoteInteraction()
  }

  disconnect() {
    if (this.chart) this.chart.destroy()
    this.chart = null
    this.teardownNoteInteraction()
  }

  // Fires on initial connect and whenever a turbo stream replaces the notes
  // JSON script tag (create/edit/date-change/delete) — keeps the dots live.
  notesTargetConnected(element) {
    this.notes = this.parseNotes(element)
    this.updateMarkers()
    if (this.chart) this.chart.update()
  }

  toggle(event) {
    this.applyVisibility(event.target)
    this.chart.update()
    this.updateMaster()
  }

  toggleAll(event) {
    const checked = event.target.checked
    this.rowToggleTargets.forEach((checkbox) => {
      checkbox.checked = checked
      this.applyVisibility(checkbox)
    })
    this.chart.update()
    event.target.indeterminate = false
  }

  applyVisibility(checkbox) {
    const indices = JSON.parse(checkbox.dataset.datasetIndices)
    indices.forEach((index) => {
      if (this.chart.data.datasets[index] !== undefined) {
        this.chart.setDatasetVisibility(index, checkbox.checked)
      }
    })
  }

  // After a legend click, reflect dataset visibility back onto the row
  // checkboxes: a row is checked while any of its datasets remain visible.
  syncCheckboxes() {
    this.rowToggleTargets.forEach((checkbox) => {
      const indices = JSON.parse(checkbox.dataset.datasetIndices)
      checkbox.checked = indices.some((index) => this.chart.isDatasetVisible(index))
    })
    this.updateMaster()
  }

  updateMaster() {
    if (!this.hasMasterToggleTarget) return

    const checkedCount = this.rowToggleTargets.filter((checkbox) => checkbox.checked).length
    this.masterToggleTarget.checked = checkedCount === this.rowToggleTargets.length
    this.masterToggleTarget.indeterminate =
      checkedCount > 0 && checkedCount < this.rowToggleTargets.length
  }

  applyCallbacks(config) {
    config.options = config.options || {}
    config.options.plugins = config.options.plugins || {}
    // Legend labels toggle their dataset like the table checkboxes do,
    // and keep the checkboxes in sync.
    config.options.plugins.legend = config.options.plugins.legend || {}
    config.options.plugins.legend.labels = {
      ...config.options.plugins.legend.labels,
      generateLabels: (chart) => {
        const items = window.Chart.defaults.plugins.legend.labels.generateLabels(chart)
        items.push({
          text: 'Notes',
          fillStyle: NOTE_COLOR,
          strokeStyle: NOTE_COLOR,
          hidden: !this.notesVisible,
          notesLegend: true,
        })
        return items
      },
    }
    config.options.plugins.legend.onClick = (_event, legendItem) => {
      if (legendItem.notesLegend) {
        this.notesVisible = !this.notesVisible
        this.chart.update()
        return
      }

      const index = legendItem.datasetIndex
      this.chart.setDatasetVisibility(index, !this.chart.isDatasetVisible(index))
      this.chart.update()
      this.syncCheckboxes()
    }
    config.options.plugins.tooltip = {
      callbacks: {
        label: (context) => {
          const dataset = context.dataset
          const suffix = dataset.yAxisID === 'y1' ? '%' : dataset.yAxisID === 'y2' ? 's' : ''
          const value = `${context.formattedValue}${suffix}`
          const compareLabel = dataset.compareLabels
            ? dataset.compareLabels[context.dataIndex]
            : null
          if (compareLabel) return `${dataset.label} — ${compareLabel}: ${value}`
          return `${dataset.label}: ${value}`
        },
      },
    }
    if (config.options.scales && config.options.scales.y1) {
      config.options.scales.y1.ticks = { callback: (value) => `${value}%` }
    }
    if (config.options.scales && config.options.scales.y2) {
      config.options.scales.y2.ticks = { callback: (value) => `${value}s` }
    }
  }

  // --- Report note markers -------------------------------------------------

  parseNotes(element) {
    try {
      return JSON.parse(element.textContent)
    } catch (e) {
      return []
    }
  }

  // Groups notes by bucket index: exact date match for daily periods,
  // year+month match for monthly periods. Notes with no matching bucket
  // are dropped.
  updateMarkers() {
    const byIndex = {}
    ;(this.notes || []).forEach((note) => {
      const index = this.bucketIndexForDate(note.date)
      if (index === -1) return
      if (!byIndex[index]) byIndex[index] = []
      byIndex[index].push(note)
    })
    this.noteMarkers = Object.entries(byIndex).map(([ index, notes ]) => ({ index: Number(index), notes }))
  }

  bucketIndexForDate(dateStr) {
    if (!this.bucketDates || this.bucketDates.length === 0) return -1

    if (this.period === 'month') {
      const yearMonth = dateStr.slice(0, 7)
      return this.bucketDates.findIndex((bucket) => bucket.slice(0, 7) === yearMonth)
    }

    if (this.period === 'week') {
      // Buckets are Groupdate week-start dates; ISO date strings sort
      // lexicographically, so a plain string compare finds the bucket
      // whose [start, nextStart) range contains the note's date.
      return this.bucketDates.findIndex((bucket, i) => {
        const nextBucket = this.bucketDates[i + 1]
        return dateStr >= bucket && (!nextBucket || dateStr < nextBucket)
      })
    }

    return this.bucketDates.indexOf(dateStr)
  }

  drawNoteMarkers(chart) {
    this.markerPositions = []
    if (!this.notesVisible) return
    if (!this.noteMarkers || this.noteMarkers.length === 0) return

    const { ctx, chartArea, scales } = chart
    const xScale = scales.x
    if (!xScale || !chartArea) return

    ctx.save()
    this.noteMarkers.forEach(({ index, notes }) => {
      const x = xScale.getPixelForValue(index)
      const y = chartArea.bottom

      ctx.beginPath()
      ctx.arc(x, y, NOTE_RADIUS, 0, Math.PI * 2)
      ctx.fillStyle = NOTE_COLOR
      ctx.fill()
      ctx.lineWidth = 1.5
      ctx.strokeStyle = '#ffffff'
      ctx.stroke()

      this.markerPositions.push({ x, y, notes })
    })
    ctx.restore()
  }

  setupNoteInteraction() {
    const container = this.canvasTarget.parentElement
    container.style.position = container.style.position || 'relative'

    this.tooltipEl = document.createElement('div')
    Object.assign(this.tooltipEl.style, {
      position: 'absolute',
      pointerEvents: 'none',
      background: '#1f2937',
      color: '#fff',
      padding: '6px 8px',
      borderRadius: '6px',
      fontSize: '12px',
      lineHeight: '1.4',
      maxWidth: '240px',
      zIndex: '20',
      display: 'none',
      boxShadow: '0 2px 6px rgba(0, 0, 0, 0.2)',
    })
    container.appendChild(this.tooltipEl)

    this.onMouseMove = (event) => this.handleMouseMove(event)
    this.onMouseLeave = () => this.hideTooltip()
    this.onClick = (event) => this.handleClick(event)
    this.canvasTarget.addEventListener('mousemove', this.onMouseMove)
    this.canvasTarget.addEventListener('mouseleave', this.onMouseLeave)
    this.canvasTarget.addEventListener('click', this.onClick)
  }

  teardownNoteInteraction() {
    if (!this.hasCanvasTarget) return

    this.canvasTarget.removeEventListener('mousemove', this.onMouseMove)
    this.canvasTarget.removeEventListener('mouseleave', this.onMouseLeave)
    this.canvasTarget.removeEventListener('click', this.onClick)
    if (this.tooltipEl) this.tooltipEl.remove()
    this.tooltipEl = null
  }

  hitTest(event) {
    if (!this.markerPositions || this.markerPositions.length === 0) return null

    const rect = this.canvasTarget.getBoundingClientRect()
    const x = event.clientX - rect.left
    const y = event.clientY - rect.top

    return this.markerPositions.find((marker) => Math.hypot(marker.x - x, marker.y - y) < HIT_RADIUS) || null
  }

  handleMouseMove(event) {
    const hit = this.hitTest(event)
    if (hit) {
      this.showTooltip(event, hit)
      this.canvasTarget.style.cursor = 'pointer'
    } else {
      this.hideTooltip()
      this.canvasTarget.style.cursor = 'default'
    }
  }

  showTooltip(event, hit) {
    const rect = this.canvasTarget.getBoundingClientRect()
    this.tooltipEl.innerHTML = hit.notes
      .map((note) => `<div><strong>${this.escapeHtml(this.formatNoteDate(note.date))}</strong>: ${this.escapeHtml(note.body)}</div>`)
      .join('')
    this.tooltipEl.style.display = 'block'
    this.tooltipEl.style.left = `${event.clientX - rect.left + 10}px`
    this.tooltipEl.style.top = `${event.clientY - rect.top - 10}px`
  }

  hideTooltip() {
    if (this.tooltipEl) this.tooltipEl.style.display = 'none'
  }

  // Clicking a dot opens the first note at that bucket for editing in the
  // modal, by navigating the report_note_modal turbo-frame to its edit URL.
  handleClick(event) {
    const hit = this.hitTest(event)
    if (!hit || hit.notes.length === 0) return

    const note = hit.notes[0]
    const frame = document.getElementById('report_note_modal')
    if (frame && note.edit_url) frame.src = note.edit_url
  }

  formatNoteDate(iso) {
    // Parse as local date (bare `new Date('YYYY-MM-DD')` is UTC and can
    // shift a day in negative-offset timezones).
    const [year, month, day] = iso.split('-').map(Number)
    return new Date(year, month - 1, day).toLocaleDateString(undefined, {
      month: 'short', day: 'numeric', year: 'numeric',
    })
  }

  escapeHtml(str) {
    const div = document.createElement('div')
    div.textContent = str == null ? '' : str
    return div.innerHTML
  }
}
